import {
  allDrawingObjects,
  drawingObjectState,
  drawingObjectWorldBounds,
  getDrawingObjectRegistryRevision
} from './drawing_object_registry.js?v=20260904f';
import { getProjectModelRevision } from '../project/project_revision_service.js';
import { invalidateRenderSceneContext } from '../render/render_scene_invalidation.mjs';

let cachedSnapshot = null;
let snapshotRevision = 0;
let snapshotBuildInProgress = false;

function nextRevision(current) {
  return current >= Number.MAX_SAFE_INTEGER ? 1 : current + 1;
}

function normalizeId(value) {
  return String(value || '').trim();
}

function canonicalDrawingObjectKey(systemId, objectId) {
  const owner = normalizeId(systemId);
  const id = normalizeId(objectId);
  return owner && id ? `${owner}:${id}` : '';
}

function drawingObjectOwnerId(object = {}, provenance = null) {
  return normalizeId(provenance?.systemId || object?.systemType || object?.systemId || object?.owner);
}

function drawingObjectId(object = {}, provenance = null) {
  return normalizeId(provenance?.objectId || object?.id);
}

function addIndexedValue(index, keyValue, value) {
  const key = normalizeId(keyValue);
  if (!key) return;
  const entries = index.get(key) || [];
  if (!entries.includes(value)) entries.push(value);
  index.set(key, entries);
}

function freezeIndexArrays(index) {
  for (const [key, values] of index.entries()) index.set(key, Object.freeze([...values]));
  return index;
}

function readOnlyMapLookup(source) {
  const facade = {
    get(key) {
      return source.get(key);
    },
    has(key) {
      return source.has(key);
    },
    entries() {
      return source.entries();
    },
    keys() {
      return source.keys();
    },
    values() {
      return source.values();
    },
    forEach(callback, thisArg) {
      return source.forEach((value, key) => callback.call(thisArg, value, key, facade));
    },
    [Symbol.iterator]() {
      return source[Symbol.iterator]();
    }
  };
  Object.defineProperty(facade, 'size', {
    enumerable: true,
    get() {
      return source.size;
    }
  });
  return Object.freeze(facade);
}

function readOnlyWeakLookup(source) {
  return Object.freeze({
    get(key) {
      return source.get(key);
    },
    has(key) {
      return source.has(key);
    }
  });
}

function normalizeBounds(bounds = null) {
  const minX = Number(bounds?.minX);
  const minZ = Number(bounds?.minZ);
  const maxX = Number(bounds?.maxX);
  const maxZ = Number(bounds?.maxZ);
  if (![minX, minZ, maxX, maxZ].every(Number.isFinite)) return null;
  return Object.freeze({
    minX: Math.min(minX, maxX),
    minZ: Math.min(minZ, maxZ),
    maxX: Math.max(minX, maxX),
    maxZ: Math.max(minZ, maxZ)
  });
}

function cloneFrozenSnapshotValue(value, seen = new WeakMap(), status = { complete: true }) {
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return seen.get(value);
  const clone = Array.isArray(value) ? [] : {};
  seen.set(value, clone);
  for (const key of Object.keys(value)) {
    try {
      clone[key] = cloneFrozenSnapshotValue(value[key], seen, status);
    } catch {
      status.complete = false;
      // Fehlerhafte Metadaten-Getter werden fail-closed ausgelassen.
    }
  }
  return Object.freeze(clone);
}

function metadataGroupIds(metadata = {}) {
  return [
    metadata.coreGroupId,
    metadata.groupId,
    metadata.connectedGroupId,
    metadata.parentGroupId,
    metadata.sourceGroupId
  ].map(normalizeId).filter(Boolean);
}

function uniqueDrawingObjects(collected = [], options = {}) {
  const byKey = new Map();
  let complete = true;
  const append = source => {
    if (!Array.isArray(source)) return;
    let length;
    try {
      length = source.length;
    } catch {
      complete = false;
      return;
    }
    for (let index = 0; index < length; index += 1) {
      try {
        const object = source[index];
        const provenance = options.provenanceByObject?.get?.(object) || null;
        const id = drawingObjectId(object, provenance);
        const key = canonicalDrawingObjectKey(drawingObjectOwnerId(object, provenance), id);
        if (key) byKey.set(key, object);
      } catch {
        complete = false;
        // Ein einzelnes fehlerhaftes DrawingObject darf den Snapshot nicht
        // fuer alle anderen System-Owner verhindern.
      }
    }
  };
  if (options.includeRegistry !== false) {
    try {
      append(allDrawingObjects());
    } catch {
      complete = false;
    }
  }
  append(collected);
  return { objects: [...byKey.values()], complete };
}

function createDrawingObjectSnapshot(objects, context = {}, options = {}) {
  const objectsByKey = new Map();
  const statesByKey = new Map();
  const boundsByKey = new Map();
  const objectsById = new Map();
  const objectsBySystem = new Map();
  const objectsByGroup = new Map();
  const objectsBySource = new Map();
  const statesById = new Map();
  const boundsById = new Map();
  const keysByLocalId = new Map();
  const statesByObject = new WeakMap();
  const boundsByObject = new WeakMap();
  let complete = true;

  for (const object of objects) {
    try {
      const provenance = options.provenanceByObject?.get?.(object) || null;
      const id = drawingObjectId(object, provenance);
      const systemId = drawingObjectOwnerId(object, provenance);
      if (!id || !systemId) continue;
      const key = canonicalDrawingObjectKey(systemId, id);
      const rawState = drawingObjectState(object);
      if (!rawState) {
        complete = false;
        continue;
      }
      const cloneStatus = { complete: true };
      const state = cloneFrozenSnapshotValue({
        ...rawState,
        id,
        systemType: systemId,
        metadata: rawState.metadata || {}
      }, new WeakMap(), cloneStatus);
      if (!cloneStatus.complete) complete = false;
      const metadata = state.metadata || {};
      const bounds = normalizeBounds(drawingObjectWorldBounds(state));
      objectsByKey.set(key, object);
      statesByKey.set(key, state);
      statesByObject.set(object, state);
      if (bounds) boundsByKey.set(key, bounds);
      if (bounds) boundsByObject.set(object, bounds);
      addIndexedValue(keysByLocalId, id, key);
      addIndexedValue(objectsBySystem, systemId, object);
      const sourceId = normalizeId(metadata.sourceId);
      if (sourceId) addIndexedValue(objectsBySource, `${systemId}:${sourceId}`, object);
      for (const groupId of metadataGroupIds(metadata)) {
        addIndexedValue(objectsByGroup, `${systemId}:${groupId}`, object);
      }
    } catch {
      complete = false;
      // Fehlerhafte Getter oder getState()-Implementierungen bleiben auf das
      // einzelne Objekt begrenzt.
    }
  }

  // Alte Verbraucher duerfen weiter ueber die lokale ID lesen, aber nur wenn
  // diese projektweit genau einem Owner gehoert. Bei einer Kollision bleibt
  // der Alias bewusst leer, damit niemals das Objekt eines fremden Systems
  // zurueckgegeben wird.
  for (const [id, keys] of keysByLocalId.entries()) {
    if (keys.length !== 1) continue;
    const key = keys[0];
    const object = objectsByKey.get(key);
    const state = statesByKey.get(key);
    const bounds = boundsByKey.get(key);
    if (object) objectsById.set(id, object);
    if (state) statesById.set(id, state);
    if (bounds) boundsById.set(id, bounds);
  }

  snapshotRevision = nextRevision(snapshotRevision);
  return Object.freeze({
    revision: snapshotRevision,
    complete,
    cacheKey: `${context.projectRevision}:${context.registryRevision}:${context.systemRevision}:${snapshotRevision}`,
    projectRevision: context.projectRevision,
    registryRevision: context.registryRevision,
    systemRevision: context.systemRevision,
    projectIdentity: context.projectIdentity,
    objects: Object.freeze([...objectsByKey.values()]),
    objectsByKey: readOnlyMapLookup(objectsByKey),
    objectsById: readOnlyMapLookup(objectsById),
    objectsBySystem: readOnlyMapLookup(freezeIndexArrays(objectsBySystem)),
    objectsByGroup: readOnlyMapLookup(freezeIndexArrays(objectsByGroup)),
    objectsBySource: readOnlyMapLookup(freezeIndexArrays(objectsBySource)),
    statesByKey: readOnlyMapLookup(statesByKey),
    statesByObject: readOnlyWeakLookup(statesByObject),
    statesById: readOnlyMapLookup(statesById),
    boundsByKey: readOnlyMapLookup(boundsByKey),
    boundsByObject: readOnlyWeakLookup(boundsByObject),
    boundsById: readOnlyMapLookup(boundsById)
  });
}

function drawingObjectSnapshotMatches(context) {
  return Boolean(
    cachedSnapshot
    && cachedSnapshot.projectRevision === context.projectRevision
    && cachedSnapshot.registryRevision === context.registryRevision
    && cachedSnapshot.systemRevision === context.systemRevision
    && cachedSnapshot.projectIdentity === context.projectIdentity
  );
}

function collectDrawingObjectSnapshot(options = {}) {
  const context = {
    projectRevision: getProjectModelRevision(),
    registryRevision: getDrawingObjectRegistryRevision(),
    systemRevision: Number(options.systemRevision) || 0,
    projectIdentity: options.projectIdentity || null
  };
  const hasAuthoritativeCollector = typeof options.collector === 'function';
  if (options.force !== true && drawingObjectSnapshotMatches(context)) return cachedSnapshot;
  if (snapshotBuildInProgress) {
    if (cachedSnapshot) return cachedSnapshot;
    const unique = uniqueDrawingObjects([], { includeRegistry: !hasAuthoritativeCollector });
    return createDrawingObjectSnapshot(unique.objects, context);
  }

  snapshotBuildInProgress = true;
  try {
    let collected = [];
    let provenanceByObject = null;
    let collectorCacheable = true;
    if (hasAuthoritativeCollector) {
      try {
        const result = options.collector();
        if (Array.isArray(result)) {
          collected = result;
        } else {
          collected = Array.isArray(result?.objects) ? result.objects : [];
          provenanceByObject = result?.provenanceByObject || null;
          collectorCacheable = Array.isArray(result?.objects) && result?.cacheable !== false;
        }
      } catch {
        collectorCacheable = false;
      }
    }
    const finalContext = {
      ...context,
      projectRevision: getProjectModelRevision(),
      registryRevision: getDrawingObjectRegistryRevision()
    };
    const unique = uniqueDrawingObjects(collected, {
      // Sobald der Registry-Facade einen owner-validierten Collector liefert,
      // ist ausschließlich dessen Ausgabe autoritativ. Sonst könnte ein
      // Plugin über die alte globale DrawingObject-Registry einen fremden
      // Owner in den Snapshot einschleusen.
      includeRegistry: !hasAuthoritativeCollector,
      provenanceByObject
    });
    const snapshot = createDrawingObjectSnapshot(unique.objects, finalContext, { provenanceByObject });
    if (collectorCacheable && unique.complete && snapshot.complete) {
      cachedSnapshot = snapshot;
    } else {
      cachedSnapshot = null;
    }
    return snapshot;
  } finally {
    snapshotBuildInProgress = false;
  }
}

function currentDrawingObjectSnapshot() {
  return cachedSnapshot;
}

function invalidateDrawingObjectSnapshot() {
  cachedSnapshot = null;
  invalidateRenderSceneContext('geometry');
}

export {
  canonicalDrawingObjectKey,
  collectDrawingObjectSnapshot,
  currentDrawingObjectSnapshot,
  invalidateDrawingObjectSnapshot
};
